type SettingsPropertiesState = {
    context: {
        settings: {
            properties?: Record<string, unknown>;
        };
    };
};

function getServerHostname(): string | undefined {
    if (typeof window !== "undefined") {
        return undefined;
    }
    try {
        const { getUrl } = require("@insite/client-framework/ServerSideRendering");
        const url = getUrl();
        if (url) {
            return url.hostname;
        }
    } catch (_e) {
        // No request context
    }
    return undefined;
}

export const isSuryaWebsite = (hostname?: string): boolean => {
    const serverHost = typeof window === "undefined" ? getServerHostname() : undefined;
    const resolvedHost = hostname || serverHost;
    if (typeof window === "undefined" && !resolvedHost) {
        return false;
    }

    const hostnameToCheck = (resolvedHost || window.location.hostname).toLowerCase();
    return (
        hostnameToCheck === "surya.com" ||
        hostnameToCheck === "www.surya.com" ||
        (hostnameToCheck.startsWith("surya.") &&
            !hostnameToCheck.includes("global-views") &&
            !hostnameToCheck.includes("globalviews") &&
            !hostnameToCheck.includes("mgbw")) ||
        (hostnameToCheck.startsWith("ezugix05sandboxaz.") &&
            !hostnameToCheck.includes("global-views") &&
            !hostnameToCheck.includes("globalviews") &&
            !hostnameToCheck.includes("mgbw")) ||
        (hostnameToCheck.startsWith("suryauk-surya.") &&
            !hostnameToCheck.includes("global-views") &&
            !hostnameToCheck.includes("globalviews") &&
            !hostnameToCheck.includes("mgbw")) ||
        (hostnameToCheck.startsWith("uk.") &&
            !hostnameToCheck.includes("global-views") &&
            !hostnameToCheck.includes("globalviews") &&
            !hostnameToCheck.includes("mgbw")) ||
        (hostnameToCheck.startsWith("surya-ezugix05sandboxaz.") &&
            !hostnameToCheck.includes("global-views") &&
            !hostnameToCheck.includes("globalviews") &&
            !hostnameToCheck.includes("mgbw")) ||
        (hostnameToCheck.startsWith("suryauk-ezugix05sandboxaz.") &&
            !hostnameToCheck.includes("global-views") &&
            !hostnameToCheck.includes("globalviews") &&
            !hostnameToCheck.includes("mgbw")) ||
        (hostnameToCheck.startsWith("ezugix04sandboxaz.") &&
            !hostnameToCheck.includes("global-views") &&
            !hostnameToCheck.includes("globalviews") &&
            !hostnameToCheck.includes("mgbw"))
    );
};

export const isMgbwWebsite = (hostname?: string): boolean => {
    const serverHost = typeof window === "undefined" ? getServerHostname() : undefined;
    const resolvedHost = hostname || serverHost;
    if (typeof window === "undefined" && !resolvedHost) {
        return false;
    }

    const hostnameToCheck = (resolvedHost || window.location.hostname).toLowerCase();
    return hostnameToCheck.includes("mgbw");
};

export const isGlobalViewsWebsite = (hostname?: string): boolean => {
    const serverHost = typeof window === "undefined" ? getServerHostname() : undefined;
    const resolvedHost = hostname || serverHost;
    if (typeof window === "undefined" && !resolvedHost) {
        return false;
    }

    const hostnameToCheck = (resolvedHost || window.location.hostname).toLowerCase();
    return hostnameToCheck.includes("global-views") || hostnameToCheck.includes("globalviews");
};

// PDP STRUCTURE only - GV layout shared with MGBW. Fonts/colors stay per-brand via theme/index.ts.
export const usesGvStylePdpLayout = (hostname?: string): boolean =>
    isGlobalViewsWebsite(hostname) || isMgbwWebsite(hostname);

/**
 * Normalizes a source brand string by converting to lowercase and removing whitespace.
 * @param value - The source brand value to normalize
 * @returns Normalized brand string (e.g., "GLOBAL VIEWS" -> "globalviews")
 */
export const normalizeSourceBrand = (value?: string | null): string => (value ?? "").toLowerCase().replace(/\s+/g, "");

/**
 * Builds a cross-website product detail path that redirects between Surya, GlobalViews, and MGBW websites
 * based on the product's source brand and the current website context.
 *
 * - Surya products viewed on GlobalViews or MGBW website â†’ redirects to Surya website
 * - GlobalViews products viewed on Surya or MGBW website â†’ redirects to GlobalViews website
 * - MGBW products viewed on Surya or GlobalViews website â†’ redirects to MGBW website
 *
 * @param basePath - The original product detail path (relative or absolute)
 * @param sourceBrand - The product's source brand (e.g., "Surya", "GLOBAL VIEWS", "MGBW")
 * @param suryaWebsiteUrl - The Surya website base URL (e.g., "https://www.surya.com")
 * @param globalViewsWebsiteUrl - The GlobalViews website base URL (e.g., "https://globalviews.surya.com")
 * @param mgbwWebsiteUrl - The MGBW website base URL (e.g., "https://mgbw.surya.com")
 * @returns The potentially redirected product detail path
 */
export const buildCrossWebsiteProductDetailPath = (
    basePath: string | undefined,
    sourceBrand: string | undefined,
    suryaWebsiteUrl: string,
    globalViewsWebsiteUrl: string,
    mgbwWebsiteUrl: string,
): string | undefined => {
    if (!basePath) {
        return basePath;
    }

    // Already absolute; keep as-is.
    if (/^https?:\/\//i.test(basePath)) {
        return basePath;
    }

    const normalized = normalizeSourceBrand(sourceBrand) || "surya";
    const isGlobalviewsProduct = normalized === "globalviews";
    const isSuryaProduct = normalized === "surya";
    // Backend sends "MG+BW" as sourceBrand; normalizeSourceBrand strips whitespace only,
    // so the + is preserved â†’ "mg+bw". Match both forms.
    const isMgbwProduct = normalized === "mgbw" || normalized === "mg+bw";

    const isOnSuryaWebsite = isSuryaWebsite();
    const isOnGlobalViewsWebsite = isGlobalViewsWebsite();
    const isOnMGBWWebsite = isMgbwWebsite();

    if (isSuryaProduct && !isOnSuryaWebsite) {
        return `${suryaWebsiteUrl}${basePath}`;
    }

    if (isGlobalviewsProduct && !isOnGlobalViewsWebsite) {
        return `${globalViewsWebsiteUrl}${basePath}`;
    }

    if (isMgbwProduct && !isOnMGBWWebsite) {
        return `${mgbwWebsiteUrl}${basePath}`;
    }

    return basePath;
};

const parseBooleanProperty = (value: unknown): boolean => {
    if (typeof value === "boolean") {
        return value;
    }

    if (typeof value === "string") {
        return value.toLowerCase() === "true";
    }

    return false;
};

export const getIsUkWebsite = (state: SettingsPropertiesState): boolean => {
    const value = state.context.settings.properties?.isUkWebsite;
    return parseBooleanProperty(value);
};

export const getHideClearanceBadge = (state: SettingsPropertiesState): boolean => {
    const value = state.context.settings.properties?.hideClearanceBadge;
    return parseBooleanProperty(value);
};

export const getDisableCheckoutContinueButtonForPickUp = (state: SettingsPropertiesState): boolean => {
    const value = state.context.settings.properties?.disableCheckoutContinueButtonForPickUp;
    return parseBooleanProperty(value);
};

type BadgeLike = {
    displayText?: string | null;
    name?: string | null;
};

export const isClearanceBadge = (badge?: BadgeLike | null): boolean => {
    const displayText = badge?.displayText?.trim().toLowerCase();
    const name = badge?.name?.trim().toLowerCase();

    return displayText === "clearance" || name === "clearance";
};
/**
 * Canonical brand key used for cart location grouping.
 */
export type CartBrandKey = "surya" | "globalviews" | "mgbw";

/**
 * Configurable shipping-origin location labels.
 * Sourced from Admin -> System Settings -> Cart Shipping Location Settings.
 */
export interface ShippingLocationLabels {
    suryaShippingLocation: string;
    globalViewsShippingLocation: string;
    mgbwShippingLocation: string;
}

/**
 * Resolves a product source brand to a canonical brand key for cart location grouping.
 * Falls back to "surya" when the brand is missing or unrecognized.
 * @param sourceBrand - The cart line source brand (e.g., "SURYA", "GLOBAL VIEWS", "MG+BW")
 */
export const resolveCartBrandKey = (sourceBrand?: string | null): CartBrandKey => {
    const normalized = normalizeSourceBrand(sourceBrand);

    if (normalized === "globalviews") {
        return "globalviews";
    }

    // Backend sends "MG+BW" as sourceBrand; normalizeSourceBrand strips whitespace only,
    // so the + is preserved. Match every known MGBW form.
    if (normalized === "mgbw" || normalized === "mg+bw" || normalized === "mitchellgold+bobwilliams") {
        return "mgbw";
    }

    return "surya";
};

/**
 * Whether an invoice belongs to the Mitchell Gold + Bob Williams legal entity.
 *
 * `brand` is the ETL invoice header brand ("SURYA" / "MG+BW"), exposed as
 * `invoice.properties.brand`. It is not tied to the storefront being browsed, so MGBW
 * invoices stay identifiable from Surya and Global Views. When the brand is missing
 * (older cached ETL responses) the active storefront is used as a fallback so the MGBW
 * site keeps its existing restriction.
 */
export const isMgbwInvoiceBrand = (brand: string | undefined | null, isMgbwStorefront: boolean): boolean => {
    if (!normalizeSourceBrand(brand)) {
        return isMgbwStorefront;
    }

    return resolveCartBrandKey(brand) === "mgbw";
};

type BrandedCartLine = { properties?: { [key: string]: string | undefined } };

/**
 * The distinct brands represented by a set of cart lines, resolved from each line's SourceBrand.
 * Lines with a missing or unrecognized brand collapse into "surya" (see resolveCartBrandKey).
 */
export const getCartBrandKeys = (cartLines: BrandedCartLine[] | undefined | null): CartBrandKey[] => [
    ...new Set((cartLines ?? []).map(line => resolveCartBrandKey(line.properties?.sourceBrand))),
];

/**
 * True when a cart holds products from more than one brand (Surya / Global Views / MGBW).
 * Each brand ships from its own warehouses, so a mixed cart has no single pick-up location.
 */
export const isMixedBrandCart = (cartLines: BrandedCartLine[] | undefined | null): boolean =>
    getCartBrandKeys(cartLines).length > 1;

/**
 * Resolves the configurable shipping-origin location label for a given source brand.
 * @param sourceBrand - The cart line source brand
 * @param labels - The configurable location labels from settings
 */
export const resolveShippingLocation = (
    sourceBrand: string | undefined,
    labels: ShippingLocationLabels,
): string => {
    switch (resolveCartBrandKey(sourceBrand)) {
        case "globalviews":
            return labels.globalViewsShippingLocation;
        case "mgbw":
            return labels.mgbwShippingLocation;
        default:
            return labels.suryaShippingLocation;
    }
};

/**
 * Cart property keys the backend persists at checkout for the per-brand payment breakdown
 * (MGBW split payment). Names mirror the segments in Extensions/Helpers/BrandSegmentation.cs
 * (SURYA / GLOBAL VIEWS / MG+BW). The leading "Brand..." segment is camelCased by the API
 * serializer while the brand suffix is preserved verbatim.
 *
 * Tax collapses SURYA + GLOBAL VIEWS into the SURYA segment; freight and white glove are
 * quoted per brand and summed.
 */
export const BRAND_PROPERTY_KEYS = {
    tax: {
        surya: "brandTax_SURYA",
        mgbw: "brandTax_MG+BW",
    },
    freight: {
        surya: "brandFreight_SURYA",
        globalViews: "brandFreight_GLOBAL VIEWS",
        mgbw: "brandFreight_MG+BW",
    },
    whiteGlove: {
        surya: "brandWhiteGlove_SURYA",
        globalViews: "brandWhiteGlove_GLOBAL VIEWS",
        mgbw: "brandWhiteGlove_MG+BW",
    },
} as const;

/** A per-brand amount parsed from a brand-prefixed cart property, with its resolved location. */
export interface BrandPrefixedAmount {
    /** The raw source brand suffix from the property key (e.g., "SURYA", "MG+BW"). */
    brand: string;
    /** The configurable shipping-origin location label for the brand. */
    location: string;
    /** The parsed decimal amount (always > 0 in the returned list). */
    amount: number;
}

/**
 * Parses per-brand amounts persisted as `{prefix}{brand}` cart properties (raw decimal strings)
 * into a list resolved to shipping locations. Entries with a missing, unparseable, or non-positive
 * amount are excluded. Used for the per-brand freight and white glove order-summary breakdowns.
 * @param properties - The cart properties dictionary (e.g., `cart.properties`).
 * @param prefix - The property-key prefix (e.g., "brandFreight_", "brandWhiteGlove_").
 * @param labels - The configurable location labels from settings.
 */
export const parseBrandPrefixedAmounts = (
    properties: { readonly [key: string]: string | undefined } | undefined,
    prefix: string,
    labels: ShippingLocationLabels,
): BrandPrefixedAmount[] =>
    Object.keys(properties ?? {})
        .filter(key => key.startsWith(prefix))
        .map(key => {
            const brand = key.slice(prefix.length);
            const raw = properties![key];
            const amount = raw ? parseFloat(raw) : 0;
            return {
                brand,
                location: resolveShippingLocation(brand, labels),
                amount: Number.isFinite(amount) ? amount : 0,
            };
        })
        .filter(entry => entry.amount > 0);
